import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, Button, Space, Checkbox, Alert } from "antd";
import {
  UserOutlined,
  MailOutlined,
  BankOutlined,
  IdcardOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  MailTwoTone,
  KeyOutlined,
} from "@ant-design/icons";
import {
  validateUserForm,
  sanitizeUserFormData,
} from "../../utils/validationHelpers";
import useBodyScrollLock from "../../../../hooks/useBodyScrollLock";
import { createUser, updateUser } from "../../../../services/userService";
import { canAssignRole } from "../../../../utils/roleHelpers";
import { useMessageApi } from "../../../../shared/providers/MessageProvider";
import "./UserFormModal.scss";

const { Option } = Select;

/**
 * User Form Modal Component
 * Modal component for creating new users or editing existing users
 *
 * @param {boolean} visible - Controls modal visibility
 * @param {Object} editingUser - User object being edited (null for create mode)
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onSuccess - Callback when user is successfully created/updated
 * @param {Array} companies - List of available companies
 * @param {Array} roles - List of available roles from backend
 */
const UserFormModal = ({
  visible,
  editingUser,
  onClose,
  onSuccess,
  companies,
  companiesLoading = false,
  roles = [],
  userInfo = null,
  canCreate = false,
  canUpdate = false,
}) => {
  const message = useMessageApi();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sendResetEmail, setSendResetEmail] = useState(true);

  const roleOptions = useMemo(() => {
    return roles
      .filter((role) => {
        const roleId = Number(role.id);
        const isAssignable = canAssignRole(userInfo, roleId, roles);
        const isCurrentRole = editingUser && Number(editingUser.role_id) === roleId;
        // Show only current role or assignable roles
        return isCurrentRole || isAssignable;
      })
      .map((role) => {
        return {
          ...role,
          disabled: false // All filtered roles are selectable
        };
      });
  }, [roles, userInfo, editingUser]);

  // Lock body scroll when modal is open
  useBodyScrollLock(visible);

  // Reset form when modal opens/closes or editing user changes
  useEffect(() => {
    if (visible && editingUser) {
      form.setFieldsValue({
        name: editingUser.name,
        email: editingUser.email,
        company: editingUser.company,
        role_id: editingUser.role_id,
        is_active: Boolean(editingUser.is_active),
      });
    } else if (visible) {
      form.resetFields();
      setSendResetEmail(true); // Reset to default when creating new user
    }
  }, [visible, editingUser, form]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Check permissions before submitting
      if (editingUser && !canUpdate) {
        message.error("You don't have permission to update users");
        return;
      }
      
      if (!editingUser && !canCreate) {
        message.error("You don't have permission to create users");
        return;
      }

      const values = await form.validateFields();
      setLoading(true);

      // Validate user data
      const validation = validateUserForm(values, !!editingUser);
      if (!validation.isValid) {
        setLoading(false);
        return;
      }

      // Sanitize user data
      const sanitizedData = sanitizeUserFormData(values);
      const selectedRoleId = Number(sanitizedData.role_id);
      const originalRoleId = editingUser ? Number(editingUser.role_id) : null;

      const isRoleChanging = editingUser
        ? selectedRoleId !== originalRoleId
        : true;

      if (isRoleChanging && !canAssignRole(userInfo, selectedRoleId, roles)) {
        message.error("You don't have permission to assign the selected role");
        setLoading(false);
        return;
      }

      // Get current user ID for tracking
      const currentUserId = userInfo?.id || null;

      try {
        let success;
        if (editingUser) {
          // Update existing user
          success = await updateUser(editingUser.id, {
            ...sanitizedData,
            user_id: currentUserId,
          });
          
          if (success) {
            message.success("User updated successfully", 2.5);
            form.resetFields();
            onSuccess();
            onClose();
          } else {
            message.error("Failed to update user", 2.5);
          }
        } else {
          // Create new user with sendResetEmail option
          const result = await createUser({
            ...sanitizedData,
            sendResetEmail: sendResetEmail,
            user_id: currentUserId,
          });
          
          if (result?.success) {
            // Intelligent feedback based on backend response
            const email = sanitizedData.email;
            
            if (result.auth0_status === "created") {
              if (result.send_reset_email_requested) {
                if (result.password_reset_email_sent) {
                  message.success(
                    result.message ||
                      `User created successfully. Password setup email sent to ${email}.`,
                    2.5
                  );
                } else {
                  message.warning(
                    result.message ||
                      `User created but email failed. Temporary password: ${result.temporary_password}`,
                    2.5
                  );
                }
              } else {
                message.success(
                  result.message ||
                    `User created successfully. Temporary password: ${result.temporary_password}`,
                  2.5
                );
              }
            } else if (result.auth0_status === "pending") {
              if (result.auth0_error) {
                const errorMessage = result.auth0_error_details || result.auth0_error;
                message.error(errorMessage, 2.5);
              } else {
                message.warning(
                  result.message || "User created in database. Auth0 setup pending.",
                  2.5
                );
              }
            } else {
              message.success(result.message || "User created successfully", 2.5);
            }
            
            form.resetFields();
            onSuccess();
            onClose();
          } else {
            message.error(result?.message || "Failed to create user", 2.5);
          }
        }
      } catch (error) {
        message.error("An error occurred. Please try again.", 2.5);
      }
    } catch (error) {
      console.error("Form validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="user-form-modal"
      width={680}
      centered={true}
      closeIcon={<CloseOutlined className="close-icon" />}
      maskClosable={false}
      keyboard={true}
      destroyOnClose={true}
      transitionName=""
      maskTransitionName=""
      getContainer={false}
      style={{ height: 'calc(100vh - 200px)' }}
    >
      {/* Modal Header */}
      <div className="modal-header">
        <div className="header-content">
          <h2 className="header-title">
            {editingUser ? "Edit User" : "Create New User"}
          </h2>
        </div>
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        <Form
          form={form}
          layout="vertical"
          className="user-form"
          requiredMark={false}
        >
          <div className="form-row">
            <Form.Item
              label="Full Name"
              name="name"
              className="form-col"
              rules={[
                { required: true, message: "Name is required" },
                { min: 2, message: "Name must be at least 2 characters" },
              ]}
            >
              <Input
                prefix={<UserOutlined className="input-icon" />}
                placeholder="Enter full name"
              />
            </Form.Item>

            <Form.Item
              label="Email Address"
              name="email"
              className="form-col"
              rules={[
                { required: true, message: "Email is required" },
                { type: "email", message: "Invalid email address" },
              ]}
            >
              <Input
                prefix={<MailOutlined className="input-icon" />}
                placeholder="Enter email address"
                disabled={!!editingUser}
              />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              label="Company"
              name="company"
              className="form-col"
              rules={[{ required: true, message: "Company is required" }]}
            >
              <Select
                placeholder={companiesLoading ? "Loading companies..." : "Select company"}
                suffixIcon={<BankOutlined className="input-icon" />}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toLowerCase() || "").includes(input.toLowerCase())
                }
                virtual={false}
                listHeight={256}
                loading={companiesLoading}
                notFoundContent={companiesLoading ? "Loading companies..." : "No companies available"}
              >
                {companies && companies.length > 0 ? (
                  companies.map((company) => (
                    <Option key={company} value={company}>
                      {company}
                    </Option>
                  ))
                ) : (
                  !companiesLoading && <Option disabled>No companies available</Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              label="Role"
              name="role_id"
              className="form-col"
              rules={[{ required: true, message: "Role is required" }]}
            >
              <Select
                placeholder="Select user role"
                suffixIcon={<IdcardOutlined className="input-icon" />}
                className="role-select-dropdown"
                popupClassName="role-dropdown-popup"
              >
                {roles.length > 0 ? (
                  roleOptions.length > 0 ? (
                    roleOptions.map((role) => (
                      <Option 
                        key={role.id} 
                        value={role.id}
                      >
                        {role.name
                          .split(" ")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase()
                          )
                          .join(" ")}
                      </Option>
                    ))
                  ) : (
                    <Option disabled>No roles available</Option>
                  )
                ) : (
                  <Option disabled>Loading roles...</Option>
                )}
              </Select>
            </Form.Item>
          </div>

          {/* Password Setup Options - Only shown when creating new user */}
          {!editingUser && (
            <div className="password-setup-section">
              <Alert
                message={
                  sendResetEmail ? (
                    <Space>
                      <MailTwoTone twoToneColor="#52c41a" />
                      <span>User will receive a password setup email</span>
                    </Space>
                  ) : (
                    <Space>
                      <KeyOutlined style={{ color: "#faad14" }} />
                      <span>A default password will be assigned and shown after creation</span>
                    </Space>
                  )
                }
                type={sendResetEmail ? "info" : "warning"}
                showIcon={false}
                style={{ marginBottom: "16px" }}
              />
              
              <Checkbox
                checked={sendResetEmail}
                onChange={(e) => setSendResetEmail(e.target.checked)}
                style={{ marginBottom: "8px" }}
              >
                <span style={{ fontWeight: 500 }}>
                  Send password setup email to user
                </span>
              </Checkbox>
              
              <div style={{ fontSize: "12px", color: "#8c8c8c", marginLeft: "24px" }}>
                {sendResetEmail ? (
                  <span>
                    ✓ User will receive an email with a secure link to set their own password
                  </span>
                ) : (
                  <span>
                    ⚠ You will need to manually share the default password with the user
                  </span>
                )}
              </div>
            </div>
          )}

          {editingUser && (
            <Form.Item
              label="Status"
              name="is_active"
              rules={[{ required: true, message: "Status is required" }]}
            >
              <Select placeholder="Select account status">
                <Option value={true}>
                  <Space>
                    <span className="status-dot active"></span>
                    <span>Active</span>
                  </Space>
                </Option>
                <Option value={false}>
                  <Space>
                    <span className="status-dot inactive"></span>
                    <span>Inactive</span>
                  </Space>
                </Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </div>

      {/* Modal Footer */}
      <div className="modal-footer">
        <Button onClick={handleClose} className="cancel-btn" disabled={loading}>
          <span className="btn-content">
            <CloseCircleOutlined />
            <span>Cancel</span>
          </span>
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          className="submit-btn"
          disabled={loading}
        >
          {loading ? (
            <span className="btn-content">
              <ReloadOutlined className="spin-icon" />
              <span>{editingUser ? "Updating..." : "Creating..."}</span>
            </span>
          ) : (
            <span className="btn-content">
              <CheckCircleOutlined />
              <span>{editingUser ? "Update User" : "Create User"}</span>
            </span>
          )}
        </Button>
      </div>
    </Modal>
  );
};

export default UserFormModal;
